import { Option } from 'effect';

import { ProductCardVariant } from '@vs-shop/commerce-ui/productCard';
import { ProductsGridItem } from '@vs-shop/commerce-ui/productsGrid';
import type { GtmListIdTypeT } from '@vs-shop/schemas/gtm/list-types';

import { resolveClientProductCardData } from '../../../product';
import type { ProductCardFields } from '../../../product/productCardFields';
import { toProductImagePreview } from '../../../product/productImagePreview';
import ProductCardRecommendationAddToCart from '../product/ProductCardRecommendationAddToCart';
import ProductCard from '../../server/product/card/ProductCard';
import ProductCardButtons from '../../server/product/card/ProductCardButtons';
import ProductCardHeader from '../../server/product/card/ProductCardHeader';
import ProductCardImage from '../../server/product/card/ProductCardImage';
import ProductCardMediaLink from '../../server/product/card/ProductCardMediaLink';
import ProductCardPriceStock from '../../server/product/card/ProductCardPriceStock';
import ProductCardStickers from '../../server/product/card/ProductCardStickers';
import ProductCardTracker from '../../server/product/card/ProductCardTracker';
import ProductCardCoupon from '../../server/product/ProductCardCoupon';

type Props = {
  readonly product: ProductCardFields;
  readonly listType: GtmListIdTypeT;
  readonly variant: ProductCardVariant;
  readonly hashIdentifier: string;
  readonly onClick: () => void;
};

/** Renders the card shared by the legacy grid and the listing journey. */
export const ProductCardTile = ({ product, listType, variant, hashIdentifier, onClick }: Props) => (
  <ProductsGridItem data-hash-identifier={hashIdentifier} onClick={onClick}>
    <ProductCardTracker listType={listType} product={resolveClientProductCardData(product)}>
      <ProductCard
        variant={variant}
        header={
          <ProductCardHeader
            productName={product.name}
            routerUrl={product.routerUrl}
            metaTitle={product.metaTitle}
          />
        }
        media={
          <ProductCardMediaLink
            routerUrl={product.routerUrl}
            metaTitle={product.metaTitle}
            variant={variant}
          >
            <ProductCardImage imagePath={product.imagePath} alt={product.name} variant={variant} />
            <ProductCardStickers
              labels={product.labels}
              ageRestriction={product.ageRestriction}
              price={product.price}
              originalPrice={product.originalPrice}
            />
          </ProductCardMediaLink>
        }
        promo={
          <ProductCardCoupon
            productCode={product.code}
            productName={product.name}
            discountBanner={product.discountBanner}
            price={product.price}
            priceWithoutTax={product.priceWithoutTax}
            stock={product.stock}
          />
        }
        description={product.subtitle ?? product.description}
        footer={
          <>
            <ProductCardPriceStock
              price={product.price}
              originalPrice={product.originalPrice}
              stock={product.stock}
              labels={product.labels}
              variant={variant}
            />
            {variant === ProductCardVariant.RECOMMENDATION ? (
              <ProductCardRecommendationAddToCart
                productCode={product.code}
                productName={product.name}
                imagePreview={toProductImagePreview(product.imagePath)}
                ageRestriction={product.ageRestriction}
                price={Option.getOrElse(product.price, () => 0)}
                priceWithoutTax={Option.getOrElse(product.priceWithoutTax, () => 0)}
                stock={product.stock}
                className="w-full px-2 text-sm tracking-normal @[320px]:text-base"
              />
            ) : (
              <ProductCardButtons
                productCode={product.code}
                productName={product.name}
                isFavourite={product.isFavourite}
                routerUrl={product.routerUrl}
                metaTitle={product.metaTitle}
                variant={variant}
              />
            )}
          </>
        }
      />
    </ProductCardTracker>
  </ProductsGridItem>
);
